import { useAppDispatch, useAppSelector } from "@/core/app/hooks"
import FORM_CONSTANT from "@/core/app/slices/uassist/form.constant"
import { saveUassistSummary } from "@/core/app/slices/uassist/formThunkApi"
import { RootState } from "@/core/app/store"
import { downloadDentalChart } from "@/core/app/slices/records/xrays"
import { setIsShowProgress } from "@/core/app/slices/clinical/clinicalSlice"
import { fetchUserProfile } from "@/core/app/slices/user/userApis"
import JsonFormBuilder from "@/rx-form/builder/JsonFormBuilder"
import {
  generateSummaryData,
  generateSummarySchema,
  generateSummaryUiSchema,
} from "@/rx-form/components/NewForm/formUtils"
import { dateFormat } from "@/core/utils/formatters"
import { Box, Grid } from "@mui/material"
import { pdf } from "@react-pdf/renderer"
import { forwardRef, useEffect, useImperativeHandle, useRef } from "react"
import { useParams } from "react-router-dom"
import { useFormContext } from "../FormContext"
import { displayMetaType } from "../types"
import { DOMToPDFComponent, extractFormDataFromDOM } from "./DOMToPDFExtractor"
import { pdfBlobToImageDataUrl } from "./pdfToImage"

export interface PdfContentRef {
  contentToPDF: React.RefObject<HTMLDivElement>
  exportComponentAsPDF: (
    contentToPDF: React.RefObject<HTMLDivElement>,
    status?: "open" | "upload",
  ) => Promise<object | undefined>
}

interface PdfContentProps {
  displayMeta?: displayMetaType
  uassistRound?: number
}

export const PdfContent = forwardRef<PdfContentRef, PdfContentProps>((props, ref) => {
  const { formName, data, schema, uiSchema, renderedTemplate } = useFormContext()
  const { displayMeta, uassistRound } = props

  const contentToPDF = useRef<HTMLDivElement>(null)

  const isRefinement = [
    FORM_CONSTANT.REFINEMENT,
    FORM_CONSTANT.REFINEMENT_2,
  ].includes(formName)

  const isRetainer = formName === FORM_CONSTANT.RETAINER

  const { dentalChart } = useAppSelector((s: RootState) => s.xrayService)
  const { patientData } = useAppSelector((s: RootState) => s.patientService)
  const { org_name, createDate } = useAppSelector((s: RootState) => s.caseService.caseDetail)
  const { uAssistOrders } = useAppSelector((s: RootState) => s.orderService)

  const dispatch = useAppDispatch()
  const { caseId, patientId } = useParams<{ caseId: string; patientId: string }>()

  /* ------------------------------------------------------------------ */
  /* Load dental chart if prescription                                  */
  /* ------------------------------------------------------------------ */
  useEffect(() => {
    if (formName === FORM_CONSTANT.PRESCRIPTION) {
      dispatch(downloadDentalChart({ patientId, caseId }))
    }
  }, [formName, patientId, caseId, dispatch])

  /* ------------------------------------------------------------------ */
  /* Load org name if missing                                           */
  /* ------------------------------------------------------------------ */
  useEffect(() => {
    if (!org_name) dispatch(fetchUserProfile())
  }, [org_name, dispatch])

  /* ------------------------------------------------------------------ */
  /* Main export function                                               */
  /* ------------------------------------------------------------------ */
  const exportComponentAsPDF = async (
    refNode: React.RefObject<HTMLDivElement>,
    status: "open" | "upload" = "upload",
  ): Promise<object | undefined> => {
    dispatch(setIsShowProgress(true))

    if (!refNode.current) {
      dispatch(setIsShowProgress(false))
      return
    }

    try {
      /* ---------------- SHOW HIDDEN RENDER ---------------- */
      refNode.current.style.visibility = "visible"
      refNode.current.style.opacity = "0"

      // wait for layout + fonts
      await new Promise(requestAnimationFrame)
      await new Promise(requestAnimationFrame)

      if (document.fonts?.ready) {
        await document.fonts.ready
      }

      /* ---------------- EXTRACT DOM ---------------- */
      const extractedRows = extractFormDataFromDOM(refNode.current)

      /* ---------------- HIDE AGAIN ---------------- */
      refNode.current.style.visibility = "hidden"

      const dateForPDF =
        status === "upload"
          ? dateFormat(new Date().toISOString(), "MM/DD/YYYY")
          : dateFormat(
            uAssistOrders.find((o) => o.uassistRound === uassistRound)?.createDate ??
            createDate ??
            "",
          )

      /* ---------------- DENTAL CHART ---------------- */
      let dentalChartDataUrl: string | undefined
      const isPrescription = formName === FORM_CONSTANT.PRESCRIPTION

      if (isPrescription && dentalChart) {
        try {
          dentalChartDataUrl = await pdfBlobToImageDataUrl(dentalChart)
        } catch {
          dentalChartDataUrl = undefined
        }
      }

      /* ---------------- BUILD PDF ---------------- */
      const blob = await pdf(
        <DOMToPDFComponent
          extractedRows={extractedRows}
          title={displayMeta?.formTitle ?? ""}
          patientId={patientId}
          patientName={`${patientData?.firstName ?? ""} ${patientData?.lastName ?? ""}`.trim()}
          caseId={caseId}
          creationDate={dateForPDF}
          orgName={org_name}
          dentalChartImage={dentalChartDataUrl}
        />,
      ).toBlob()

      /* ---------------- OPEN OR UPLOAD ---------------- */
      const openOrUpload = async (
        b: Blob,
        st: "open" | "upload",
        uploadHandler?: (blob: Blob) => Promise<any>,
      ) => {
        const pdfName = isRetainer ? "retainer-summary.pdf" : "summary.pdf"

        
        if (st === "open") {
          const url = URL.createObjectURL(b)
          window.open(url, "_blank")
          return
        }

        if (uploadHandler) return uploadHandler(b)

        const formData = new FormData()
        formData.append("attachment", new File([b], pdfName, { type: "application/pdf" }))

        return dispatch(
          saveUassistSummary({
            patientId: patientId || patientData.id,
            caseId,
            payload: formData,
            isRefinement,
            isRetainer,
          }),
        )
      }

      if (isRefinement) {
        if (status === "open") return openOrUpload(blob, "open")
        return openOrUpload(blob, "upload", (b) => placeUassistOrderRefinement(b))
      }

      return openOrUpload(blob, status)
    } catch (err) {
      console.error("PDF error", err)
      return
    } finally {
      dispatch(setIsShowProgress(false))
      if (refNode.current) refNode.current.style.visibility = "hidden"
    }
  }

  /* ------------------------------------------------------------------ */
  /* Refinement upload helper                                           */
  /* ------------------------------------------------------------------ */
  const placeUassistOrderRefinement = (blob: Blob) => {
    const formData = new FormData()
    formData.append("attachment", new File([blob], `refinement.pdf`, { type: "application/pdf" }))
    formData.append("is_refinement", "1")

    return dispatch(
      saveUassistSummary({
        patientId: patientId || patientData.id,
        caseId,
        payload: formData,
        isRefinement: true,
      }),
    )
  }

  useImperativeHandle(ref, () => ({
    contentToPDF,
    exportComponentAsPDF,
  }))

  /* ------------------------------------------------------------------ */
  /* Hidden responsive render container                                 */
  /* ------------------------------------------------------------------ */
  return (
    <Grid>
      <Box
        ref={contentToPDF}
        sx={{
          position: "fixed",
          top: 0,
          left: 0,
          opacity: 0,
          pointerEvents: "none",
          width: 800,
          maxWidth: "100%",
          zIndex: -1,
        }}
      >
        <section id="pdf-summary-section" style={{ width: "100%", maxWidth: 800 }}>
          <JsonFormBuilder
            schema={generateSummarySchema(schema, renderedTemplate?.schema || {})}
            uischema={generateSummaryUiSchema(
              uiSchema,
              data,
              renderedTemplate?.uiSchema || {},
              true,
            )}
            defaultData={generateSummaryData({
              ...data,
              ...(renderedTemplate?.data || {}),
            })}
            isFormSubmitted={false}
            setIsFormSubmitted={() => { }}
          />
        </section>
      </Box>
    </Grid>
  )
})
